import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";

import {
  DonationPlatform,
  GetDonationDetailsQuery,
  GetDonationDetailsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import { GET_DONATION_DETAILS } from "~frontend/graphql/donationHistory";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { formatDateTime } from "~frontend/util/dateFormat";
import { formatDollars } from "~frontend/util/formatDollars";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

import { DONATION_RECURRING_TYPES_LABELS } from "./recurringLabels";
import { DonationHistory } from "./types";

export function DonationDetailsDialog({
  donation,
  onClose,
}: {
  donation: DonationHistory;
  onClose: () => void;
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;

  const getDonationDetailsQuery = useQuery<
    GetDonationDetailsQuery,
    GetDonationDetailsQueryVariables
  >(GET_DONATION_DETAILS, {
    variables: { input: { donationHistoryId: donation.id } },
  });

  return (
    <Dialog open maxWidth="sm">
      <DialogTitle>Donation Details</DialogTitle>
      <DialogContent>
        <Box sx={{ backgroundColor: palette.lightestGray, padding: "24px" }}>
          <AsyncWrapper
            data={getDonationDetailsQuery.data}
            loading={getDonationDetailsQuery.loading}
            errors={getDonationDetailsQuery.error}
          >
            {data => {
              const recurringText =
                DONATION_RECURRING_TYPES_LABELS[
                  data.getDonationDetails.recurringType
                ];

              return (
                <TwoColumnDisplay
                  rows={[
                    {
                      key: "donation-amount",
                      label: "Donation Amount",
                      content: (
                        <>
                          <Typography>
                            {formatDollars(
                              data.getDonationDetails.amountInCents / 100
                            )}
                          </Typography>
                          {recurringText ? (
                            <Typography>{recurringText}</Typography>
                          ) : null}
                        </>
                      ),
                    },
                    {
                      key: "donation-date",
                      label: "Donation Date",
                      content: formatDateTime(
                        data.getDonationDetails.donatedAt
                      ),
                    },
                    {
                      key: "donor-name",
                      label: "Donor Name",
                      content: `${data.getDonationDetails.firstName ?? "(Unknown)"} ${data.getDonationDetails.lastName ?? "(Unknown)"}`,
                    },
                    {
                      key: "phone-number",
                      label: "Phone Number",
                      content: data.getDonationDetails.phoneNumber
                        ? prettyPhoneNumber(data.getDonationDetails.phoneNumber)
                        : "(Unknown)",
                    },
                    {
                      key: "address",
                      label: "Address",
                      content:
                        data.getDonationDetails.address1 ||
                        data.getDonationDetails.address2 ||
                        data.getDonationDetails.city ||
                        data.getDonationDetails.state ||
                        data.getDonationDetails.zipCode ? (
                          <FlexBox direction="column">
                            {data.getDonationDetails.address1 ? (
                              <Typography>
                                {data.getDonationDetails.address1}
                              </Typography>
                            ) : null}
                            {data.getDonationDetails.address2 ? (
                              <Typography>
                                {data.getDonationDetails.address2}
                              </Typography>
                            ) : null}
                            {data.getDonationDetails.city ||
                            data.getDonationDetails.state ||
                            data.getDonationDetails.zipCode ? (
                              <Typography>
                                {data.getDonationDetails.city
                                  ? `${data.getDonationDetails.city}, `
                                  : ""}
                                {data.getDonationDetails.state}{" "}
                                {data.getDonationDetails.zipCode}
                              </Typography>
                            ) : null}
                          </FlexBox>
                        ) : (
                          "(Unknown)"
                        ),
                    },
                    {
                      key: "source",
                      label: "Source",
                      content: (
                        <FlexBox direction="column">
                          <Typography>
                            {
                              { [DonationPlatform.ActBlue]: "ActBlue" }[
                                data.getDonationDetails.platform
                              ]
                            }
                          </Typography>
                          {data.getDonationDetails.formName ? (
                            <Typography>
                              Form: {data.getDonationDetails.formName}
                            </Typography>
                          ) : null}
                        </FlexBox>
                      ),
                    },
                    {
                      key: "campaign",
                      label: "Campaign",
                      content: data.getDonationDetails.campaign ? (
                        <InternalLink
                          to={subaccountLink(
                            `/campaigns/${data.getDonationDetails.campaign.shortId}`
                          )}
                          target="_blank"
                        >
                          {data.getDonationDetails.campaign.name}
                        </InternalLink>
                      ) : (
                        "(Unknown)"
                      ),
                    },
                  ]}
                />
              );
            }}
          </AsyncWrapper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="tertiary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
